import { useEffect, useState } from "react";
import { Badge } from "@zweb/ui";
import { apiFetch } from "../lib/api";

interface Training {
  id: string;
  title: string;
  description: string;
  type: string;
  category: string;
  status: string;
  scheduledAt: string | null;
  duration: number | null;
  recordingUrl: string | null;
  createdAt: string;
}

const typeLabels: Record<string, string> = {
  online: "Online",
  presencial: "Presencial",
  video: "Vídeo",
  document: "Documento",
};

const statusLabels: Record<string, string> = {
  scheduled: "Agendado",
  in_progress: "Em Andamento",
  completed: "Concluído",
  cancelled: "Cancelado",
};

const statusColors: Record<string, string> = {
  scheduled: "bg-accent-100 text-accent-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const categoryLabels: Record<string, string> = {
  iniciando: "Iniciando",
  modulos: "Módulos",
  integracoes: "Integrações",
  relatorios: "Relatórios",
  admin: "Administração",
  avancado: "Avançado",
};

export default function TrainingsPage() {
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    apiFetch("/api/trainings")
      .then((json) => {
        if (json.success) {
          setTrainings(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/dashboard" className="text-sm font-medium hover:text-accent-600">Dashboard</a>
            <a href="/chamados" className="text-sm font-medium hover:text-accent-600">Chamados</a>
            <a href="/downloads" className="text-sm font-medium hover:text-accent-600">Downloads</a>
            <a href="/licencas" className="text-sm font-medium hover:text-accent-600">Licenças</a>
            <a href="/treinamentos" className="text-sm font-medium text-accent-600">Treinamentos</a>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Sair
            </button>
          </nav>
        </div>
      </header>

      <main className="container py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Treinamentos</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Acesse treinamentos e materiais de capacitação
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {trainings.length === 0 ? (
            <div className="col-span-full rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-950">
              <p className="text-gray-600 dark:text-gray-400">
                Nenhum treinamento disponível no momento.
              </p>
            </div>
          ) : (
            trainings.map((training) => (
              <div
                key={training.id}
                className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
              >
                <div className="flex items-start justify-between">
                  <Badge className={statusColors[training.status]}>
                    {statusLabels[training.status]}
                  </Badge>
                  <Badge variant="secondary">{typeLabels[training.type]}</Badge>
                </div>

                <h3 className="mt-4 text-lg font-semibold">{training.title}</h3>
                {training.description && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {training.description}
                  </p>
                )}

                <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
                  {training.category && (
                    <span>{categoryLabels[training.category] || training.category}</span>
                  )}
                  {training.duration && (
                    <span>{training.duration} min</span>
                  )}
                </div>

                {training.scheduledAt && (
                  <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                    Agendado para: {new Date(training.scheduledAt).toLocaleDateString("pt-BR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                )}

                {training.recordingUrl && (
                  <a
                    href={training.recordingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-block text-accent-600 hover:underline"
                  >
                    Assistir gravação
                  </a>
                )}

                <button className="mt-4 w-full rounded-lg bg-accent-600 px-4 py-2 font-medium text-white hover:bg-accent-700">
                  {training.status === "completed" ? "Revisar" : "Acessar"}
                </button>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
